import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useRestApi } from "@/context/api";
import { useCurrentCompany } from "@/context/auth";
import { CreateRoleDefinitionPayload, UpdateRoleDefinitionPayload } from "@/lib/dto";
import { CompanyRoles, RoleDefinitionDto, RoleTemplateDto } from "@/types/auth";

import { queryKeys } from "./queryKeys";

/** `enabled: false` for an admin who may not read roles, where the list is only extra detail */
export const useRolesQuery = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { getRoles } = useRestApi().roles;
  const currentCompany = useCurrentCompany();

  return useQuery({
    enabled,
    queryKey: [queryKeys.ROLE_DEFINITIONS, currentCompany.id],
    queryFn: async () => {
      const res = await getRoles(currentCompany.id);
      res.roles.sort((a, b) => a.name.localeCompare(b.name));
      return res;
    },
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const useRoleDetailsQuery = (id: string) => {
  const { getRoleDefinition } = useRestApi().roles;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.ROLE_DEFINITION_DETAILS, id, currentCompany.id],
    queryFn: () => getRoleDefinition(id, currentCompany.id),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const usePermissionsQuery = () => {
  const { getCompanyPermissions } = useRestApi().roles;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.PERMISSIONS, currentCompany.id],
    queryFn: () => getCompanyPermissions(currentCompany.id),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

/** Not retried: UserManager never caches a failed lookup, so each retry would wait on a silent SkillPanel again. */
export const useSkillPanelDashboardsQuery = () => {
  const { getSkillPanelDashboards } = useRestApi().roles;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.SKILLPANEL_DASHBOARDS, currentCompany.id],
    queryFn: () => getSkillPanelDashboards(currentCompany.id),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    retry: false,
  });
};

export const useRoleTemplatesQuery = () => {
  const { getRoleTemplates } = useRestApi().roles;

  return useQuery<RoleTemplateDto[]>({
    queryKey: [queryKeys.ROLE_TEMPLATES],
    queryFn: getRoleTemplates,
    staleTime: 5 * 60 * 1000,
  });
};

// MUTATIONS
export const useCreateRoleMutation = () => {
  const { createRole } = useRestApi().roles;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateRoleDefinitionPayload) => createRole(payload, currentCompany.id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ROLE_DEFINITIONS, currentCompany.id],
        refetchType: "active",
      });
    },
  });
};

export const useUpdateRoleMutation = (id: string) => {
  const { updateRole } = useRestApi().roles;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateRoleDefinitionPayload) =>
      updateRole(payload, id, currentCompany.id),
    onSuccess: (result) => {
      queryClient.setQueryData<CompanyRoles>(
        [queryKeys.ROLE_DEFINITIONS, currentCompany.id],
        (old) => {
          if (!old) {
            return;
          }
          const roles = old?.roles.map((role) => {
            if (role.id === (result as { id?: string }).id) {
              return { ...role, ...result };
            }
            return role;
          });
          return { ...old, roles };
        },
      );
      queryClient.setQueryData<RoleDefinitionDto>(
        [queryKeys.ROLE_DEFINITION_DETAILS, id, currentCompany.id],
        () => result,
      );
    },
  });
};
export const useRolePermissionsMutation = (id: string) => {
  const { updateRole } = useRestApi().roles;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateRoleDefinitionPayload) =>
      updateRole(payload, id, currentCompany.id),
    onSuccess: (result) => {
      queryClient.setQueryData<RoleDefinitionDto>(
        [queryKeys.ROLE_DEFINITION_DETAILS, id, currentCompany.id],
        () => result,
      );

      queryClient.invalidateQueries({
        queryKey: [queryKeys.ROLE_DEFINITION_DETAILS],
      });
    },
  });
};

export const useDeleteRoleMutation = (id: string) => {
  const { deleteRole } = useRestApi().roles;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteRole(id, currentCompany.id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ROLE_DEFINITIONS, currentCompany.id],
        refetchType: "active",
      });
    },
  });
};
